import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

type OrgRole = 'owner' | 'admin' | 'member' | 'contratado';

interface UserOrgInfo {
  role: OrgRole | null;
  orgId: string | null;
  orgName: string | null;
  hasPendingRequest: boolean;
  permissions: {
    canAddMembers: boolean;
    canViewData: boolean;
    canEditData: boolean;
    canDeleteData: boolean;
  };
}

export function useOrgPermissions() {
  const { user } = useAuth();
  const [userOrgInfo, setUserOrgInfo] = useState<UserOrgInfo>({
    role: null,
    orgId: null,
    orgName: null,
    hasPendingRequest: false,
    permissions: {
      canAddMembers: false,
      canViewData: false,
      canEditData: false,
      canDeleteData: false
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🎯 useOrgPermissions useEffect triggered', { user: !!user, email: user?.email });
    
    if (!user) {
      console.log('❌ Sem usuário, resetando userOrgInfo');
      setUserOrgInfo({
        role: null,
        orgId: null,
        orgName: null,
        hasPendingRequest: false,
        permissions: {
          canAddMembers: false,
          canViewData: false,
          canEditData: false,
          canDeleteData: false
        }
      });
      setLoading(false);
      return;
    }

    console.log('✅ Usuário encontrado, chamando fetchUserOrgInfo');
    // Garantir que exiba estado de carregamento durante a busca para evitar redirecionamentos prematuros
    setLoading(true);
    fetchUserOrgInfo();
  }, [user]);

  const fetchUserOrgInfo = async () => {
    // Ativa o loading imediatamente ao iniciar a busca
    setLoading(true);
    if (!user) {
      console.log('❌ Usuário não encontrado no fetchUserOrgInfo');
      setLoading(false);
      return;
    }

    console.log('🔍 Buscando informações da organização para:', user.email, 'ID:', user.id);

    try {
      // Get user's organization membership - check for any active membership first
      console.log('🔍 Buscando qualquer membership ativo para o usuário...');
      
      const { data: membershipData, error: membershipError } = await supabase
        .from('org_memberships')
        .select(`
          role,
          org_id,
          organizations (name)
        `)
        .eq('user_id', user.id)
        .eq('active', true)
        .order('role', { ascending: false }) // owner > admin > member
        .limit(1)
        .maybeSingle();

      console.log('✅ Resultado da busca por membership:', membershipData);
      console.log('🔍 Error na busca por membership:', membershipError);

      if (membershipError) {
        console.error('❌ Erro na query:', membershipError);
        setLoading(false);
        return;
      }

      if (!membershipData) {
        console.log('❌ Usuário não tem membership, verificando se confirmou email...');
        
        // Verificar se o usuário confirmou o email
        if (!user.email_confirmed_at) {
          console.log('📧 Usuário não confirmou email ainda');
          setUserOrgInfo({
            role: null,
            orgId: null,
            orgName: null,
            hasPendingRequest: false,
            permissions: {
              canAddMembers: false,
              canViewData: false,
              canEditData: false,
              canDeleteData: false
            }
          });
          setLoading(false);
          return;
        }

        console.log('✅ Email confirmado, verificando na tabela de contratação...');
        console.log('🔍 Email do usuário:', user.email);
        
        // Check if user exists in contratacao table with more specific logging
        try {
          const { data: contratacaoData, error: contratacaoError } = await supabase
            .from('contratacao')
            .select('email, full_name, stage')
            .eq('email', user.email)
            .maybeSingle();
          
          console.log('📊 Resultado da query contratacao:', { contratacaoData, contratacaoError });
          
          if (contratacaoError) {
            console.error('❌ Erro na query de contratação:', contratacaoError);
          }
          
          if (contratacaoData) {
            console.log('✅ Usuário encontrado na tabela de contratação, definindo como contratado');
            setUserOrgInfo({
              role: 'contratado',
              orgId: null,
              orgName: null,
              hasPendingRequest: false,
              permissions: {
                canAddMembers: false,
                canViewData: false,
                canEditData: false,
                canDeleteData: false
              }
            });
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('❌ Erro ao buscar contratação:', error);
        }
        
        // Check if user has a pending organization request
        console.log('🔍 Verificando solicitações pendentes de organização...');
        try {
          const { data: pendingRequest, error: requestError } = await supabase
            .from('organization_requests')
            .select('id, organization_name, status')
            .eq('email', user.email)
            .eq('status', 'pending')
            .maybeSingle();
          
          console.log('📋 Resultado da busca por solicitação pendente:', { pendingRequest, requestError });
          
          if (pendingRequest) {
            console.log('⏳ Usuário tem solicitação pendente');
            setUserOrgInfo({
              role: null,
              orgId: null,
              orgName: null,
              hasPendingRequest: true,
              permissions: {
                canAddMembers: false,
                canViewData: false,
                canEditData: false,
                canDeleteData: false
              }
            });
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('❌ Erro ao buscar solicitações pendentes:', error);
        }
        
        console.log('❌ Usuário não encontrado nem em organizações nem em contratação');
        setUserOrgInfo({
          role: null,
          orgId: null,
          orgName: null,
          hasPendingRequest: false,
          permissions: {
            canAddMembers: false,
            canViewData: false,
            canEditData: false,
            canDeleteData: false
          }
        });
        setLoading(false);
        return;
      }

      const role = membershipData.role as OrgRole;
      const orgId = membershipData.org_id;
      const orgName = (membershipData.organizations as any)?.name || null;

      // Get admin permissions if user is admin
      let adminPermissions = null;
      if (role === 'admin') {
        const { data: permData } = await supabase
          .from('admin_permissions')
          .select('*')
          .eq('user_id', user.id)
          .eq('org_id', orgId)
          .maybeSingle();

        adminPermissions = permData;
      }

      // Determine permissions based on role
      let permissions = {
        canAddMembers: false,
        canViewData: false,
        canEditData: false,
        canDeleteData: false
      };

      switch (role) {
        case 'owner':
          permissions = {
            canAddMembers: true,
            canViewData: true,
            canEditData: true,
            canDeleteData: true
          };
          break;
        case 'admin':
          permissions = {
            canAddMembers: adminPermissions?.can_add_members || false,
            canViewData: adminPermissions?.can_view_data || true,
            canEditData: adminPermissions?.can_edit_data || false,
            canDeleteData: adminPermissions?.can_delete_data || false
          };
          break;
        case 'member':
          permissions = {
            canAddMembers: false,
            canViewData: true,
            canEditData: false,
            canDeleteData: false
          };
          break;
        case 'contratado':
          permissions = {
            canAddMembers: false,
            canViewData: false,
            canEditData: false,
            canDeleteData: false
          };
          break;
      }

      console.log('🎯 Definindo userOrgInfo:', {
        role,
        orgId,
        orgName,
        permissions
      });

      setUserOrgInfo({
        role,
        orgId,
        orgName,
        hasPendingRequest: false,
        permissions
      });
    } catch (error) {
      console.error('Error fetching user org info:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasOrgRole = (requiredRole: OrgRole | OrgRole[]) => {
    if (!userOrgInfo.role) return false;
    
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(userOrgInfo.role);
    }
    
    return userOrgInfo.role === requiredRole;
  };

  const canAccessIntranet = userOrgInfo.role && ['owner', 'admin', 'member'].includes(userOrgInfo.role);

  const shouldRedirectToContratacao = userOrgInfo.role === 'contratado';

  const shouldRedirectToEmailConfirmation = user && !user.email_confirmed_at && !loading;

  const shouldRedirectToPendingRequest = userOrgInfo.hasPendingRequest && !loading;

  const shouldRedirectToNoOrg = !userOrgInfo.role && !userOrgInfo.hasPendingRequest && user?.email_confirmed_at && !loading;

  console.log('🎯 Retornando useOrgPermissions:', {
    userOrgInfo,
    loading,
    canAccessIntranet,
    shouldRedirectToContratacao,
    shouldRedirectToEmailConfirmation,
    shouldRedirectToPendingRequest,
    shouldRedirectToNoOrg
  });

  return {
    userOrgInfo,
    loading,
    hasOrgRole,
    canAccessIntranet,
    shouldRedirectToContratacao,
    shouldRedirectToEmailConfirmation,
    shouldRedirectToPendingRequest,
    shouldRedirectToNoOrg,
    refetch: fetchUserOrgInfo
  };
}